// Region model for per-region SSR.
//
// Two axes are kept deliberately separate:
//   - REGION  -> price / currency / availability. Indexed, SSR'd into the
//                region HTML, self-canonical. One of: 'us' | 'eu' | 'au' | 'world'.
//   - COUNTRY -> transit time + ships-from. Client-side delta keyed by a
//                country cookie. NEVER in the URL, never indexed.
//
// LOCKED DECISIONS (see REGION-SSR-PROPOSAL.md + task brief):
//   - Indexed regions = 4: us / eu / au / world (path prefixes /us/, /eu/,
//     /au/, /world/).
//   - NO /uk/ — the UK is billed EUR at EU pricing, so it routes to the EU
//     store. The UK is a shipping country only.
//   - /world is the EUR ex-VAT EXPORT store for every country the backend's
//     authoritative country map assigns store="world" (NO/CH/IS/JP/KR + the
//     long tail). The backend `store` field is the SINGLE SOURCE OF TRUTH for
//     store membership — never re-derived here (see utils/countryStore.js). The
//     legacy countryToRegion() below is a SYNC FALLBACK only (hydration-safe
//     client paths where the async backend map is not yet loaded).

import commerceSlugs from '../content/commerce-slugs.json'

// The 4 indexed regions, in display order. `world` is the EUR ex-VAT export
// store (priced against a representative served export country — see
// regionToFetchCountry).
export const REGIONS = ['us', 'eu', 'au', 'world']

// Default region for bare (no-prefix) URLs. Crawlers always get a fully
// priced page from the default region.
export const DEFAULT_REGION = 'us'

// Region prefix matcher for the catch-all router.
export const REGION_PREFIX_REGEX = /^(us|eu|au|world)$/

// Cookie name for the resolved region (set on accept/dismiss of the geo banner
// and on explicit region switch).
export const REGION_COOKIE = 'region'

// Cookie name for the precise shipping country (transit/ships-from delta).
export const COUNTRY_COOKIE = 'shippingCountry'

export function isRegion(value) {
  return typeof value === 'string' && REGIONS.includes(value.toLowerCase())
}

/**
 * Map an ISO country code to one of the indexed regions.
 *
 *   US, CA                -> us
 *   AU, NZ                -> au
 *   everything else       -> eu   (incl. GB/UK + MX + Europe)
 *
 * IMPORTANT: this is the LEGACY, SYNC FALLBACK only. The AUTHORITATIVE
 * country->store mapping now comes from the backend country map
 * (utils/countryStore.js -> storeForCountry), which is the single source of
 * truth and is the ONLY thing that knows about the /world store. This function
 * is retained for hydration-safe CLIENT code paths that must compute a region
 * synchronously before the async backend map has loaded (e.g. getRegionFrom
 * Storage, useCurrentRegion). It deliberately does NOT return 'world' — a
 * world-store country falls back to 'eu' (the same EUR currency) until the
 * backend map resolves the precise store, so the sync fallback never shows a
 * wrong currency.
 *
 * NOTE: MX (Mexico) routes to EU, NOT US. Confirmed against the backend: MX is
 * billed in EUR, ships from Ireland (EU warehouse), and uses the EU Stripe
 * account — it is intentionally an EU-store customer (ShippingRegion default
 * branch -> EU; ZoneTableEu maps MX -> EU_ZONE_9 with a fixed EUR120 shipping).
 * Routing MX -> /us/ would display USD but charge EUR at checkout (a mismatch).
 */
export function countryToRegion(countryCode) {
  if (!countryCode) {
    return DEFAULT_REGION
  }

  const cc = String(countryCode).toUpperCase()

  switch (cc) {
    case 'US':
    case 'CA':
      return 'us'
    case 'AU':
    case 'NZ':
      return 'au'
    default:
      // GB/UK + MX + all of Europe + rest of world -> EU store (EUR pricing).
      return 'eu'
  }
}

/**
 * Representative country code used for the SSR *price* fetch of a region.
 *
 *   us    -> 'US'
 *   au    -> 'AU'
 *   eu    -> 'DE'
 *   world -> 'NO'  (Norway: a genuine EUR-export, served=true market. The
 *                   backend prices NO in EUR with taxIncluded=null — i.e. ex-VAT
 *                   export pricing — which is exactly the /world catalog.)
 */
export function regionToFetchCountry(region) {
  switch (String(region || '').toLowerCase()) {
    case 'us':
      return 'US'
    case 'au':
      return 'AU'
    case 'eu':
      return 'DE'
    case 'world':
      return 'NO'
    default:
      return 'US'
  }
}

const DISPLAY = {
  us: { label: 'United States', currency: 'USD', symbol: '$' },
  eu: { label: 'Europe', currency: 'EUR', symbol: '€' },
  au: { label: 'Australia', currency: 'AUD', symbol: '$' },
  // /world: EUR ex-VAT export pricing for the rest of the served world.
  world: { label: 'International', currency: 'EUR', symbol: '€' },
}

/**
 * Human-facing display metadata for a region. Currency is DERIVED from the
 * region and cannot be set independently.
 */
export function regionDisplay(region) {
  return DISPLAY[String(region || '').toLowerCase()] || DISPLAY[DEFAULT_REGION]
}

/**
 * Strip a leading region segment ("/us/shop" -> { region, path: "/shop" }).
 * Accepts a path string. Returns the matched region (or null) and the
 * region-less path (always leading-slashed, never trailing-slashed except "/").
 */
export function stripRegionFromPath(pathname) {
  const raw = pathname || '/'
  const segments = raw.split('/').filter(Boolean)
  if (segments.length && REGION_PREFIX_REGEX.test(segments[0].toLowerCase())) {
    const region = segments[0].toLowerCase()
    const rest = '/' + segments.slice(1).join('/')
    return { region, path: rest === '/' ? '/' : rest.replace(/\/$/, '') }
  }
  return { region: null, path: raw }
}

/**
 * Build the region-prefixed equivalent of a (possibly already-prefixed) path.
 * Used by the footer switcher and the country dropdown to preserve the current
 * path while swapping the region prefix.
 *
 * The region prefix is ALWAYS included — including for the default region — so
 * every region (us/eu/au) renders at an explicit /<region>/... URL.
 */
export function pathWithRegion(pathname, region) {
  const { path } = stripRegionFromPath(pathname)
  const target = isRegion(region) ? String(region).toLowerCase() : DEFAULT_REGION

  if (path === '/' || path === '') {
    return `/${target}`
  }
  return `/${target}${path}`
}

// Set of region-stripped, slash-trimmed slugs that the region-SSR router treats
// as COMMERCE (price-bearing / priced-surface pages). Generated from the local
// dataset by web/content/_gen-commerce-slugs.mjs — keep that script's commerce
// _type list in sync with pageRendersPrices/pageRendersSurfaces in
// web/pages/[[...slug]].js. Home ('') is commerce only if it appears here.
const COMMERCE_SLUGS = new Set(commerceSlugs)

/**
 * True iff `path` points at a COMMERCE page. Strips any leading region prefix
 * and leading/trailing slashes, then tests the resulting slug against the
 * generated commerce set. '' / '/' map to the home slug ('').
 */
export function isCommercePath(path) {
  const { path: bare } = stripRegionFromPath(path || '/')
  const slug = String(bare).replace(/^\/+|\/+$/g, '')
  return COMMERCE_SLUGS.has(slug)
}

/**
 * Region-aware href for a nav/content link, scoped to the region of the page
 * being rendered (`region`).
 *   - CONTENT path -> always bare (no region prefix).
 *   - COMMERCE path on a REGION page (explicit region) -> region-prefixed
 *     (e.g. /eu/shop) so it never 302-redirects through the bare URL.
 *   - COMMERCE path on a CONTENT page (no/invalid region, e.g. an FAQ, blog
 *     post or legal doc) -> BARE (e.g. /shop) so the bare-URL geo-IP redirect
 *     (cookie -> geo-IP -> us) resolves the visitor's region at click time,
 *     instead of hardcoding the default region into the link.
 */
export function regionalHref(path, region) {
  if (!isCommercePath(path)) {
    return path
  }
  return isRegion(region) ? pathWithRegion(path, region) : path
}

// Hosts we treat as our own site. An absolute URL on one of these is INTERNAL
// and gets reduced to its pathname so it can be region-scoped (commerce) or
// left bare (content). Everything else is external and never touched.
const INTERNAL_HOSTS = new Set(['vrs.racing', 'www.vrs.racing', 'staging.vrs.racing'])

/**
 * Normalize a link href that may come from CMS rich text or nav config, so that
 * INTERNAL links carry the right region scope and EXTERNAL links are untouched.
 *
 *   - Falsy / empty                       -> returned unchanged.
 *   - mailto: / tel: / bare #anchor       -> returned unchanged.
 *   - external host (appspot, socials, …) -> returned unchanged.
 *   - root-relative ('/…')                -> regionalHref(path, region).
 *   - absolute on an INTERNAL_HOST        -> reduced to pathname (+ query/hash),
 *                                            then regionalHref(path, region).
 *
 * regionalHref yields a region prefix for commerce paths and a bare path for
 * content paths. Any ?query / #hash is preserved and re-appended AFTER scoping.
 *
 * Pure + SSR-safe: no `window`; URL parsing is guarded and falls back to
 * treating the href as external on failure.
 */
export function normalizeInternalHref(href, region) {
  if (!href || typeof href !== 'string') {
    return href
  }

  // Non-navigational / explicitly external schemes and bare anchors: leave as-is.
  if (/^(mailto:|tel:|#)/i.test(href)) {
    return href
  }

  // Root-relative path: already internal, just scope it (preserving query/hash).
  if (href.startsWith('/') && !href.startsWith('//')) {
    const { path, suffix } = splitPathSuffix(href)
    return regionalHref(path, region) + suffix
  }

  // Absolute (or protocol-relative) URL: parse to inspect the host.
  let url
  try {
    // A base lets `new URL` resolve protocol-relative ('//host/…') inputs.
    url = new URL(href, 'https://vrs.racing')
  } catch (e) {
    return href
  }

  if (!INTERNAL_HOSTS.has(url.hostname.toLowerCase())) {
    return href
  }

  return regionalHref(url.pathname, region) + url.search + url.hash
}

// Split a root-relative href into its path and its ?query#hash suffix, so the
// path can be region-scoped without the suffix interfering with slug matching.
function splitPathSuffix(href) {
  const match = /[?#]/.exec(href)
  if (!match) {
    return { path: href, suffix: '' }
  }
  return { path: href.slice(0, match.index), suffix: href.slice(match.index) }
}
